import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  ProofTaskListResponseSchema,
  ProofTaskSchema,
  type PatchProofTaskInput,
  type ProofTask,
} from "@/api/types/proof";
import {
  AssetListResponseSchema,
  AssetSchema,
  type Asset,
  type CreateAssetInput,
  type DeleteAssetInput,
  type PatchAssetInput,
} from "@/api/types/asset";
import { InquirySchema, type Inquiry } from "@/api/types/inquiry";

const EventReferralsResponseSchema = z.object({ items: z.array(InquirySchema) });

/**
 * Phase 6's event-scoped hooks: post-event proof-cadence tasks, media
 * assets, and the read-only referral list — all namespaced under a single
 * event, mirroring the query-key convention from useEvents.ts/useInvoices.ts
 * (list + detail-scoped keys, invalidate-the-whole-namespace on mutation).
 */

export const proofKeys = {
  all: ["proof"] as const,
  tasks: (eventId: string | undefined) => [...proofKeys.all, "tasks", eventId] as const,
};

export const assetKeys = {
  all: ["assets"] as const,
  list: (eventId: string | undefined) => [...assetKeys.all, "list", eventId] as const,
};

export const referralKeys = {
  all: ["referrals"] as const,
  list: (eventId: string | undefined) => [...referralKeys.all, "list", eventId] as const,
};

// ---- Proof-cadence tasks ---------------------------------------------------

export function useProofTasks(eventId: string | undefined) {
  return useQuery<ProofTask[]>({
    queryKey: proofKeys.tasks(eventId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/events/${eventId}/proof-tasks`);
      return validateResponse(
        ProofTaskListResponseSchema,
        data,
        "GET /v2/events/:id/proof-tasks",
      ).items;
    },
    enabled: !!eventId,
    staleTime: 10 * 1000,
  });
}

export function usePatchProofTask() {
  const queryClient = useQueryClient();
  return useMutation<ProofTask, unknown, PatchProofTaskInput>({
    mutationFn: async ({ eventId, taskId, ...patch }) => {
      const { data } = await apiClient.patch(
        `/v2/events/${eventId}/proof-tasks/${taskId}`,
        patch,
      );
      // Contract returns the patched ProofTask itself (unlike the T-minus
      // task PATCH, which returns the parent Event) — there's no other
      // event field this endpoint could touch, so a bare task response is
      // the simplest shape here.
      return validateResponse(
        ProofTaskSchema,
        data,
        "PATCH /v2/events/:id/proof-tasks/:task_id",
      );
    },
    onSuccess: (_task, variables) => {
      void queryClient.invalidateQueries({ queryKey: proofKeys.tasks(variables.eventId) });
    },
  });
}

// ---- Assets -----------------------------------------------------------------

export function useEventAssets(eventId: string | undefined) {
  return useQuery<Asset[]>({
    queryKey: assetKeys.list(eventId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/events/${eventId}/assets`);
      return validateResponse(
        AssetListResponseSchema,
        data,
        "GET /v2/events/:id/assets",
      ).items;
    },
    enabled: !!eventId,
    staleTime: 10 * 1000,
  });
}

function invalidateAssets(queryClient: QueryClient, eventId: string) {
  void queryClient.invalidateQueries({ queryKey: assetKeys.list(eventId) });
}

export function useAddAsset() {
  const queryClient = useQueryClient();
  return useMutation<Asset, unknown, CreateAssetInput>({
    mutationFn: async ({ eventId, ...body }) => {
      const { data } = await apiClient.post(`/v2/events/${eventId}/assets`, body);
      return validateResponse(AssetSchema, data, "POST /v2/events/:id/assets");
    },
    onSuccess: (_asset, variables) => invalidateAssets(queryClient, variables.eventId),
  });
}

export function usePatchAsset() {
  const queryClient = useQueryClient();
  return useMutation<Asset, unknown, PatchAssetInput>({
    mutationFn: async ({ eventId, assetId, ...patch }) => {
      const { data } = await apiClient.patch(
        `/v2/events/${eventId}/assets/${assetId}`,
        patch,
      );
      return validateResponse(AssetSchema, data, "PATCH /v2/events/:id/assets/:asset_id");
    },
    onSuccess: (_asset, variables) => invalidateAssets(queryClient, variables.eventId),
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, DeleteAssetInput>({
    mutationFn: async ({ eventId, assetId }) => {
      await apiClient.delete(`/v2/events/${eventId}/assets/${assetId}`);
    },
    onSuccess: (_void, variables) => invalidateAssets(queryClient, variables.eventId),
  });
}

// ---- Referrals (read-only) --------------------------------------------------

/** Inquiries whose `referral_event_id` points back at this event. */
export function useEventReferrals(eventId: string | undefined) {
  return useQuery<Inquiry[]>({
    queryKey: referralKeys.list(eventId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/events/${eventId}/referrals`);
      return validateResponse(
        EventReferralsResponseSchema,
        data,
        "GET /v2/events/:id/referrals",
      ).items;
    },
    enabled: !!eventId,
    staleTime: 15 * 1000,
  });
}
